import type { NoticeWithType } from "../../../components/ViewNotices";
import { getSidebarItem } from "../../../config/sidebarConfig";
import { noticeHref } from "../../../routes";
import { api } from "../../../services/api";
import { noticesQuery, usersQuery } from "../../../services/queries";
import { viewQuery } from "../scopedQuery";
import { viewNoticesHref } from "../../../utils/circleNavigation";
import {
  filterNoticesForFeed,
  getReadableSenderName,
  noticesBySenderHref,
  scheduledNoticesHref,
  splitNoticesForFeed,
  urgentNoticesHref,
} from "../../../utils/noticeFeedFilters";
import { formatScheduleWhen } from "../../../utils/noticeScheduling";
import { normalizeViewCount, resolveNoticeAudienceSize } from "../../../utils/viewCount";
import { dayLabel, findDateRange, isInRange } from "../dateRange";
import { askedHow } from "./askedHow";
import { getNoticeEventBounds } from "../../../utils/noticeCalendarEvents";
import { toNotice } from "../../notices/noticeHandover";
import { normalise, stripLeadingGreeting } from "../smallTalk";
import { significantWords } from "../significantWords";
import { askFor, normal } from "./findByName";
import { loadCircles, pickCircle } from "./pickCircle";
import { openPageEntry } from "./openPage";
import { AnswerContext, defineEntry, HandoffEntry, isSlotQuestion } from "./types";
import {
  capitalize,
  countOf,
  countSentence,
  Dated,
  datedList,
  during,
  isAre,
  listOf,
  listUpTo,
  offerThisWeek,
  quoted,
  wasWere,
} from "./wording";

const noticesPage = getSidebarItem("notices");
const linkToNotices = () => ({ label: `Open ${noticesPage.label}`, href: noticesPage.path });

/** "Who posted this?" needs a title. "Filter by sender" is the All Senders control. */
function senderHowToText(asked: string): string {
  if (/\bwho posted\b/i.test(asked)) {
    return "Which Notice do you mean? Tell me its title and I can point you at the sender. Notices also has an All Senders filter.";
  }
  return "Use All Senders on Notices. That shows the Notices from one person. You don't need a title for that.";
}

const NO_CIRCLES = "You don't have any Circles yet, so there are no Notices to show by Circle.";
const NO_SENT = "You haven't sent any Notices yet.";
const NOT_SURE_WHICH = "I'm not sure which Notice you mean. Tell me its title, like “the Debs Ball notice”.";

const notices = (n: number) => countOf(n, "Notice");
const scheduled = (n: number) => countOf(n, "scheduled Notice");

/** Notices' three lists, from the same query and rules as the Notices page. */
export async function loadFeed({ user, token, queryClient }: AnswerContext) {
  const all = await queryClient.fetchQuery(viewQuery(user, noticesQuery(user?.schoolId, token)));
  const { publishedNotices, scheduledNotices } = splitNoticesForFeed(all as NoticeWithType[]);
  return {
    // Newest first, as the Sent tab shows them
    sent: [...publishedNotices].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    ),
    scheduled: scheduledNotices,
  };
}

export const noticeLink = (notice: NoticeWithType) => ({
  label: "Open the Notice",
  href: noticeHref(notice.noticeId),
  state: { notice },
});

const titlesOf = (list: NoticeWithType[]) => listUpTo(list.map((n) => quoted(n.title)));

/** Words of a question about a sent Notice that aren't part of its name. */
const VIEW_QUESTION_WORDS = new Set([
  "how", "many", "much", "who", "which", "has", "have", "had", "did", "was", "were", "get", "got",
  "people", "person", "parent", "familie", "family", "user", "member", "anyone", "everyone", "someone",
  "view", "viewed", "viewer", "seen", "see", "saw", "read", "opened", "open", "look", "looked", "at",
  "notice", "message", "announcement", "post", "sent", "send", "count", "number", "total", "time",
  "percent", "percentage", "rate", "show", "tell", "check", "my", "our", "we", "us", "you",
  "last", "latest", "newest", "recent", "most", "about", "called", "titled", "named", "that", "this",
  "so", "far", "yet", "by", "with", "one", "out", "went", "gone",
  // Contractions, stripped of their apostrophe by significantWords: never part of a Notice's name
  "cant", "wont", "dont", "isnt", "arent", "wasnt", "werent", "doesnt", "didnt", "havent", "hasnt", "hadnt",
  "reminder", "remind", "reminded", "nudge", "need", "want", "please", "could", "would",
  "poll", "polls", "vote", "voted", "voting", "rsvp", "rsvped", "said", "coming", "attending",
  "closed", "close", "result", "results", "breakdown", "came", "answer", "answered", "choice", "chose",
  "still", "come", "as", "go", "goes", "turn", "turned",
]);

/** Words of "when is it?" that are never part of the Notice's name. */
const WHEN_WORDS = new Set(["when", "time", "day", "date", "happen"]);

/**
 * "day", "date" and "time" are stripped from the score so "what day is the trip?"
 * still finds Zoo trip. They come back only to split a tie: "Sports Day" then
 * beats "Sports kit", because "day" is in that title and not the other.
 */
const CALENDAR_TITLE_WORDS = new Set(["day", "date", "time"]);

/**
 * Extra words around a "when is …?" question. They are not the Notice's name:
 * "when will our blackbrick apartment be ready" is about Blackbrick.
 */
const WHEN_FILLER = new Set([
  "will", "ready", "next", "upcoming", "soon", "event", "events", "hi", "hey", "hello", "gonna", "going",
  "coming", "up",
]);

/**
 * Words many titles share, so on their own they never name one Notice when it's mentioned in passing:
 * "Move it to Friday" isn't "Kara Photo Day is Friday 19th of June".
 */
const GENERIC_TITLE_WORDS = new Set([
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday", "weekend", "today", "tomorrow",
  "january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december",
  "day", "week", "month", "term", "year", "morning", "afternoon", "evening", "night", "last", "first", "new", "old", "same",
  "grade", "class", "school", "parents", "parent", "families", "notice", "notices", "lesson", "lessons", "meeting", "update",
  "reminder", "other", "one", "all", "everyone",
]);

/** What kind of thing an Event is: on its own, never enough to name which one. */
const EVENT_NOUNS = new Set([
  "party", "ball", "evening", "presentation", "conference", "trip", "concert", "show", "sale", "fair", "night", "dinner",
  "lunch", "breakfast", "picnic", "assembly", "training", "workshop", "match", "game", "gala", "camp", "outing", "tour", "practice",
]);

/**
 * "Booking" is how someone refers to the event, the way "event" is.
 * It stays a name only when it is the only word left ("when is our booking?"),
 * so a notice titled "Booking Blackbrick" can still be found that way.
 * "when is our booking at the venue" is about that venue, even if another
 * notice title contains "booking".
 */
const BOOKING_WORDS = new Set(["booking", "reservation", "appointment"]);

/** The name words in a question, without the words that ask about a Notice rather than name it. */
const nameWordsOf = (said: string) =>
  Array.from(significantWords(said.replace(/['’]s\b/gi, ""))).filter((w) => !VIEW_QUESTION_WORDS.has(w) && !WHEN_WORDS.has(w));

/** Name words left once "when will it be ready?" and "our next event" are taken off. */
const whenNameWords = (said: string) => {
  const words = nameWordsOf(said).filter((w) => !WHEN_FILLER.has(w));
  const specific = words.filter((w) => !BOOKING_WORDS.has(w));
  return specific.length > 0 ? specific : words;
};

/** Calendar words in the question that also sit in a title, used only to break a tie. */
const calendarMarks = (said: string) =>
  Array.from(significantWords(said)).filter((word) => CALENDAR_TITLE_WORDS.has(word));

function breakTitleTie<T extends { title: string }>(found: T[], said: string): T[] {
  const numbers = Array.from(said.matchAll(/\b(\d{1,2})(?:st|nd|rd|th)?\b/gi), (m) => m[1]);
  if (numbers.length && found.length > 1) {
    const numbered = found.filter((notice) => numbers.every((number) => new RegExp(`\\b${number}(?:st|nd|rd|th)?\\b`, "i").test(notice.title)));
    if (numbered.length) found = numbered;
  }
  const marks = calendarMarks(said);
  if (marks.length === 0 || found.length < 2) return found;
  const narrowed = found.filter((notice) => {
    const title = significantWords(notice.title);
    return marks.some((word) => title.has(word));
  });
  return narrowed.length === 0 ? found : narrowed;
}

const saidAfterGreeting = (text: string) => stripLeadingGreeting(text.toLowerCase().replace(/[^a-z0-9' ]+/g, " ").replace(/\s+/g, " ").trim());

/**
 * "When is our next event?": the question names no Notice, so the answer is the
 * next one coming up. A how-to that merely mentions an Event ("Can parents RSVP
 * to an event?") is not this.
 */
export function asksForNextEvent(text: string): boolean {
  const said = saidAfterGreeting(text);
  if (whenNameWords(said).length !== 0) return false;
  return /\b(?:next|upcoming)\s+events?\b/.test(said) || /\bwhens?\b[^.]*\bevents?\b/.test(said);
}

/**
 * Words around "see my events" that are not a Notice's name.
 * "I don't want a calendar, I want to view my upcoming events" is still the list.
 */
const SEE_EVENTS_FILLER = new Set([
  "dont",
  "not",
  "no",
  "nope",
  "want",
  "need",
  "see",
  "view",
  "show",
  "look",
  "my",
  "our",
  "page",
  "calendar",
  "list",
  "tell",
  "well",
  "just",
]);

/** "no the events page": open Calendar, rather than list what is on it. */
export function wantsTheEventsPage(text: string): boolean {
  const said = saidAfterGreeting(text);
  return /^(?:(?:no|nope|not that)\s+)?(?:the\s+)?events?\s+pages?$/.test(said);
}

/**
 * "I need to see my events" and "view my upcoming events": the upcoming list,
 * not a named Notice and not a how-to that only mentions an Event.
 */
export function wantsUpcomingEvents(text: string): boolean {
  if (wantsTheEventsPage(text)) return false;
  if (asksForNextEvent(text)) return true;
  const said = saidAfterGreeting(text);
  if (/^(?:what|how) about (?:the |my |our )?events?$/.test(said)) return true;
  if (!/\bevents?\b/.test(said)) return false;
  if (/\b(?:rsvp|cancel|create|add|remind|poll|edit|delete)\b/.test(said)) return false;
  const names = whenNameWords(said).filter((word) => !SEE_EVENTS_FILLER.has(word));
  return names.length === 0 && /\b(?:see|view|show|upcoming|my|our|need)\b/.test(said);
}

/**
 * The sent Notice a question names, or null when it doesn't name one.
 * One whose title has most of the name's words ("sports day" isn't "Heritage Day"),
 * and of those the one they make up the largest part of.
 */
/** The title they typed in full, so "Booking Blackbrick Umhlanga" is not also the launch. */
function titledExactly(sent: NoticeWithType[], said: string): NoticeWithType | null {
  const target = normalise(said);
  if (!target) return null;
  const found = sent.filter((notice) => normalise(notice.title) === target);
  return found.length === 1 ? found[0] : null;
}

function pickNamedNotice(sent: NoticeWithType[], said: string) {
  const exact = titledExactly(sent, said);
  if (exact) return { notice: exact };
  const asked = nameWordsOf(said);
  if (asked.length === 0 || sent.length === 0) return null;

  const share = (n: NoticeWithType) => {
    const title = significantWords(n.title);
    const shared = asked.filter((w) => title.has(w)).length;
    return shared / asked.length > 0.5 && title.size > 0 ? shared / title.size : 0;
  };
  const best = Math.max(...sent.map(share));
  if (best === 0) return null;
  const found = breakTitleTie(sent.filter((n) => share(n) === best), said);
  if (found.length > 1) {
    return askFor("notice", "ambiguous", "Which Notice do you mean?", found.map((n) => n.title));
  }
  return { notice: found[0] };
}

/**
 * A "when is …?" question. Words that aren't in any title ("apartment", "ready")
 * are ignored, and a title has to contain every word that is. "joburg trip"
 * finds “Grade 4 Joburg Trip”; "blackbrick" finds the Blackbrick Notices.
 */
function pickNoticeForWhen(sent: NoticeWithType[], said: string) {
  const exact = titledExactly(sent, said);
  if (exact) return { notice: exact };
  // A stated day selects the occurrence before shorter titles can win the word score.
  const dateKey = findDateRange(said);
  if (dateKey && /^(?:date|dom):/.test(dateKey)) {
    const [kind, value] = dateKey.split(":");
    const [month, day] = kind === "date" ? value.split("-").map(Number) : [new Date().getMonth() + 1, Number(value)];
    const year = said.match(/\b((?:19|20)\d{2})\b/)?.[1];
    sent = sent.filter((notice) => {
      const event = getNoticeEventBounds(notice);
      const at = event?.start ?? new Date(sentAt(notice));
      return at.getMonth() + 1 === month && at.getDate() === day && (!year || at.getFullYear() === Number(year));
    });
    if (!year) {
      const thisYear = sent.filter((notice) => (getNoticeEventBounds(notice)?.start ?? new Date(sentAt(notice))).getFullYear() === new Date().getFullYear());
      if (thisYear.length) sent = thisYear;
    }
  }
  const asked = whenNameWords(saidAfterGreeting(said));
  if (asked.length === 0 || sent.length === 0) return null;
  const inSomeTitle = (word: string) => sent.some((n) => significantWords(n.title).has(word));
  const grounded = asked.filter(inSomeTitle);
  if (grounded.length === 0) return null;
  // Not asked as "when is …?" ("Move it to Friday", "I said Grade 4", "plan a year of lessons"): a Notice is only
  // named when most of what was said is in its title, and that's more than a day, a grade or a common word
  const askedWhen = ASKING_WHEN.test(saidAfterGreeting(said));
  if (!askedWhen && (grounded.length * 2 < asked.length || grounded.every((word) => GENERIC_TITLE_WORDS.has(word)))) return null;
  // "When is the zebra party?": only "party" is in a title, and "zebra" names a different one; "when is the ball?" still finds Debs Ball
  if (askedWhen && grounded.length < asked.length && grounded.every((word) => GENERIC_TITLE_WORDS.has(word) || EVENT_NOUNS.has(word))) return null;

  const share = (n: NoticeWithType) => {
    const title = significantWords(n.title);
    const shared = grounded.filter((w) => title.has(w)).length;
    return shared === grounded.length && title.size > 0 ? shared / title.size : 0;
  };
  const best = Math.max(...sent.map(share));
  if (best === 0) return null;
  const found = breakTitleTie(sent.filter((n) => share(n) === best), said);
  if (found.length > 1) {
    return askFor("notice", "ambiguous", "Which Notice do you mean?", found.map((n) => n.title));
  }
  return { notice: found[0] };
}

/**
 * The sent Notice a question names: see `pickNamedNotice`. With no name, the latest one.
 * A name that matches nothing asks which Notice they mean.
 */
export function pickNotice(sent: NoticeWithType[], said: string) {
  const named = pickNamedNotice(sent, said);
  if (named) return named;
  if (nameWordsOf(said).length === 0) return { notice: sent[0] };
  const titles = Array.from(new Set(sent.map((n) => n.title)));
  return askFor("notice", "notFound", "I couldn't find that Notice. Which one do you mean?", titles);
}

/** The title among these that the message names, when it names exactly one. */
export function titleNamedIn(said: string, titles: string[]): string | null {
  const fake = titles.map((title, index) => ({ title, noticeId: `mentioned-${index}` }) as NoticeWithType);
  const named = pickNamedNotice(fake, said);
  return named && !isSlotQuestion(named) ? named.notice.title : null;
}

/** The question itself is "when is …?", not a how-to that happens to contain "when". */
const ASKING_WHEN = /^(?:when|what time|what day|what date)\b/;
const ABOUT_LEAD = /^(?:(?:and|now|ok(?:ay)?|also)\s+)?(?:what|how)\s+about\s+/i;

/** "How do I…?" and "Create an event…" stay catalogue questions, not a title search. */
const NOT_A_TITLE_SEARCH = /^(?:how|why|where|which|who|can|could|do|does|did|is|are|was|were|has|have|had|will|what|whats|create|send|add|make|invite|upload|delete|schedule|remind|start|write|draft|open|show|filter|change|edit|remove)\b/i;

/**
 * "I have a dinner coming up" and "at the artistry" name a Notice without
 * starting "when is". A how-to or a task does not.
 */
function namesANoticeInPassing(said: string): boolean {
  if (NOT_A_TITLE_SEARCH.test(said)) return false;
  if (/\bcoming up\b/i.test(said) || /\b(?:i|we)\s+have\b/i.test(said)) return true;
  const words = whenNameWords(said);
  return words.length > 0 && words.length <= 8;
}

/** The message is the title, not a new request that happens to share a word with it. */
function isJustTheTitle(text: string, title: string): boolean {
  const words = significantWords(text);
  const titleWords = significantWords(title);
  if (words.size === 0 || titleWords.size === 0) return false;
  let shared = 0;
  words.forEach((w) => {
    if (titleWords.has(w)) shared += 1;
  });
  return shared === words.size && shared / titleWords.size > 0.5;
}

/**
 * What to look up when this message is about a Notice already in the conversation,
 * or asks when something is. The exact title when the last answer named it;
 * otherwise the message itself, for a search of sent Notices. Null when this
 * isn't a question about one of them.
 */
export function noticeFollowUpQuery(text: string, mentions?: string[]): string | null {
  const said = saidAfterGreeting(text).replace(ABOUT_LEAD, "").trim();
  if (asksForNextEvent(text)) return null;
  const lookup = said.match(/^(?:(?:can|could)\s+you\s+)?(?:find|look\s+up|pull\s+up|show\s+me|i'?m\s+looking\s+for|do\s+we\s+have\s+(?:a\s+)?notice\s+about)\s+(.+)$/i);
  if (lookup) {
    if (/\b(?:drive|files?|folders?|documents?)\b/i.test(said)) return null;
    return `when is ${lookup[1].replace(/\s+thing$/i, "")}`;
  }
  // "Is Dinner at Artistry one of ours?" names the Notice to look up
  const ours = said.match(/^is\s+(.+?)\s+(?:one of ours|ours|one of our events)[?.!]*$/i);
  if (ours) return ours[1];
  if (mentions?.length) {
    const named = titleNamedIn(said, mentions);
    if (named && (ASKING_WHEN.test(said) || isJustTheTitle(said, named))) return named;
  }
  if (ASKING_WHEN.test(said) || namesANoticeInPassing(said)) return said;
  return null;
}

/** "Which one?" after an answer that named several Notices. */
export function whichMentioned(mentions: string[]): string {
  return `Which one do you mean? Name it, for example ${quoted(mentions[0])}.`;
}

const clock = (date: Date) => date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

/**
 * The same facts three ways, in one voice. The day comes first. The title is
 * said once, without quotes. "Event" is kept for a Notice that has no start
 * time, so it's clear why.
 */
export function whenWordings(notice: NoticeWithType): [string, string, string] {
  // "Kara Photo Day is Friday 19th of June." is said without its full stop, so the sentence still reads
  const bare = notice.title.replace(/[.!\s]+$/, "");
  // A title that is a sentence already ("Photo Day is Friday 19th of June") is quoted, so "is on …" still reads
  const title = /\s(?:is|are|was|will)\s/i.test(bare) ? `“${bare}”` : bare;
  const bounds = getNoticeEventBounds(notice);
  const kind = notice.type as string;
  const isEvent = kind === "event" || kind === "custom_event" || Boolean(bounds);
  if (bounds) {
    const when = bounds.allDay ? dayLabel(bounds.start) : `${dayLabel(bounds.start)} at ${clock(bounds.start)}`;
    // "Parents Evening was on …" once it has happened
    const over = (bounds.end ?? bounds.start).getTime() < Date.now();
    if (over) {
      return [`${title} was on ${when}.`, `That was ${when}, for ${title}.`, `${when}. That was ${title}.`];
    }
    return [
      `${title} is on ${when}.`,
      `That's ${when}, for ${title}.`,
      `${when}. That's ${title}.`,
    ];
  }
  if (isEvent) {
    return [
      `${title} doesn't have a day or time yet.`,
      `No day or time is set for ${title} yet.`,
      `I can see ${title}, and the day still isn't set.`,
    ];
  }
  const at = notice.publishAt || notice.createdAt;
  const sent = at ? new Date(at) : null;
  if (sent && !Number.isNaN(sent.getTime())) {
    const day = dayLabel(sent);
    return [
      `${title} went out on ${day}. It isn't an Event, so it has no event time.`,
      `You sent ${title} on ${day}. It isn't an Event, so there's no time to be there.`,
      `${title} was sent on ${day}. It isn't an Event, so I don't have a start time.`,
    ];
  }
  return [
    `${title} isn't an Event, so it has no day or time.`,
    `${title} has no event time. It isn't an Event.`,
    `There's no day or time on ${title}, because it isn't an Event.`,
  ];
}

/** The sent Notice a question names, loading the feed first; null when nothing's been sent yet. */
async function findNamedNotice(said: string, ctx: AnswerContext) {
  const { sent } = await loadFeed(ctx);
  return sent.length === 0 ? null : pickNotice(sent, said);
}

/** A sent or scheduled Notice named in a "when is …?" question. Null when the message doesn't name one. */
export async function findNoticeForWhen(said: string, ctx: AnswerContext) {
  const { sent, scheduled } = await loadFeed(ctx);
  const notices = [...sent, ...scheduled];
  if (notices.length === 0) return null;
  return pickNoticeForWhen(notices, said);
}

/** Its Views as its card on the Notices list shows them: seen, out of who it went to. */
export async function viewsOf(notice: NoticeWithType, ctx: AnswerContext) {
  const subcircles = (await loadCircles(ctx)) as Parameters<typeof resolveNoticeAudienceSize>[1];
  const circle = subcircles.find((c) => c.circleId === notice.targetSubcircle);
  const needsMemberCount =
    notice.audienceTotal === undefined &&
    !notice.targetUserId?.trim() &&
    !Number.isFinite(Number(circle?.memberCount));
  const { user, token, queryClient } = ctx;
  const members = needsMemberCount
    ? await queryClient.fetchQuery(viewQuery(user, usersQuery(user?.schoolId || user?.orgId, token)))
    : [];
  return normalizeViewCount({
    seen: notice.readCount ?? 0,
    total:
      notice.audienceTotal ??
      resolveNoticeAudienceSize(notice.targetSubcircle, subcircles, members.length, notice.targetUserId),
  });
}

/** When a sent Notice went out: when it was published if it was scheduled, otherwise when it was made. */
const sentAt = (notice: NoticeWithType) => notice.publishAt || notice.createdAt;

/** A Notice's Viewers, as its own page lists them: "Viewers (x of y)". */
async function viewersOf(notice: NoticeWithType, ctx: AnswerContext) {
  const { user, token } = ctx;
  if (!user?.schoolId || !token || !notice.noticeId) return { names: [] as string[], total: 0 };
  const reads = await api.notices.getReads(notice.noticeId, user.schoolId, token, {
    targetSubcircle: notice.targetSubcircle,
  });
  const names = (reads.users ?? []).map((u) => u.name).filter(Boolean);
  return { names, total: normalizeViewCount({ seen: names.length, total: reads.total }).total };
}

/** Whether a Notice has its own choice results: a Poll, or an Event with RSVP turned on. */
const hasChoiceResults = (notice: NoticeWithType) =>
  (notice.type as string) === "poll" || (notice.type === "event" && notice.rsvpEnabled === true);

type Choice = { label: string; count: number; percentage: number };

/** "Yes: 12 (60%), No: 8 (40%)" */
const choicesList = (choices: Choice[]) => listOf(choices.map((c) => `${c.label}: ${c.count} (${c.percentage}%)`));

/** A Notice's Poll or RSVP results, as its own page shows them. `choices` is null when it isn't a Poll or RSVP Event. */
async function pollResultsOf(notice: NoticeWithType, ctx: AnswerContext) {
  if (!hasChoiceResults(notice) || !ctx.token || !notice.noticeId) {
    return { choices: null as Choice[] | null, total: 0, closed: false };
  }
  const results = await api.notices.getPollResults(notice.noticeId, ctx.token);
  return {
    choices: results.options.map((o) => ({ label: o.label, count: o.count, percentage: o.percentage })),
    total: results.totalVotes,
    closed: results.isClosed,
  };
}


const TITLES = /^(mr|mrs|ms|miss|dr|sir|madam|mx)$/;

/** The words of a person's name, without titles like "Mrs". */
const nameWords = (name: string) => normal(name).split(" ").filter((w) => w && !TITLES.test(w));

/**
 * The sender a question names: the one sharing the most words of their name
 * with it, so "Mrs Naidoo" finds Priya Naidoo. "I" and "my" mean the person asking.
 */
function pickSender(senders: string[], said: string, me?: string) {
  const asked = new Set(nameWords(said));
  const mine = me && senders.includes(me) && /\b(i|me|my|myself)\b/i.test(said) ? me : null;
  if (mine) return { sender: mine };
  const share = (sender: string) => nameWords(sender).filter((w) => asked.has(w)).length;
  const best = Math.max(0, ...senders.map(share));
  if (best === 0) {
    // A capitalised word after the first is likely a name we don't know
    const named = said.trim().split(/\s+/).slice(1).some((w) => /^[A-Z]/.test(w) && w !== "I");
    return named
      ? askFor("sender", "notFound", "I couldn't find anyone by that name on your Notices. Whose Notices do you mean?", senders)
      : askFor("sender", "missing", "Whose Notices would you like to see?", senders);
  }
  const found = senders.filter((s) => share(s) === best);
  if (found.length > 1) return askFor("sender", "ambiguous", "Who do you mean?", found);
  return { sender: found[0] };
}


const urgent = (n: number) => countOf(n, "Urgent Notice");

/** Sent ones first, then those still to go out, with when they go. */
const urgentList = (sent: string[], scheduled: Dated[]) =>
  listUpTo([
    ...sent.map(quoted),
    ...scheduled.map(({ title, when }) => `${quoted(title)} (going out ${when})`),
  ]);

type Views = { title: string; seen: number; total: number; percentage: number };

/** "12 of 30 (40%)", as the card on Notices shows it */
const viewsText = ({ seen, total, percentage }: Views) => `${seen} of ${total} (${percentage}%)`;

/** "Send a reminder for the trip notice": opens that Notice with Send reminder ready. Nothing is sent on its own. */
const sendReminderEntry: HandoffEntry = {
  id: "notices.sendReminder",
  sidebarItem: "notices",
  examples: [
    "Send a reminder for the trip notice",
    "Send a reminder about the camp letter",
    "Can you send a reminder for the fees notice?",
    "Remind parents about the uniform notice",
    "Send a reminder for the excursion notice",
    "I need to send a reminder about the concert",
    "Send a reminder for the civvies day notice",
    "Remind everyone about the swimming gala notice",
    "Can we send a reminder for the sports day notice?",
    "Send out a reminder for the parents evening notice",
    "Nudge parents about the trip notice",
    "Send a reminder for my last notice",
    "Can you send the reminder for me now?",
  ],
  handoff: async (said, ctx) => {
    const { sent } = await loadFeed(ctx);
    if (sent.length === 0) return { kind: "cannot", text: NO_SENT };
    const pick = pickNotice(sent, said.notice ?? "");
    if (isSlotQuestion(pick)) {
      return { ...pick, text: "Which Notice should the reminder be for? Nothing is sent until you press Send reminder." };
    }
    const { notice } = pick;
    return {
      kind: "ready",
      text: `Here's the Notice ${quoted(notice.title)} to send a reminder for. Nothing is sent until you press Send reminder.`,
      summary: [{ label: "Notice", value: notice.title }],
      to: toNotice(notice, { openReminder: true }),
    };
  },
};

export const noticesEntries = [
  openPageEntry("notices", "It's where you view and manage all notices for your Organization. Sent lists the ones already out.", [
    "Where can I see the notices we've sent?",
    "Where are notices I already sent?",
    "Where are my notices?",
    "Show me sent notices",
    "show sent notices",
    "Where would I see it if I had sent it?",
    "i cant find yesterdays announcement",
    "Where are polls listed?",
    "Where do I find a poll?",
    "I'll check Notices",
    "list what we already posted",
    "Sent is under Notices",
    "Find my notices",
    "Find a sent notice",
    "Find a sent post",
    "Show me my notices",
    "Find my posts",
    "Look up my notices",
  ]),

  defineEntry({
    id: "notices.reminderHowTo",
    sidebarItem: "notices",
    examples: [
      "How do I send a reminder about a notice?",
      "How do I remind parents about a notice I already sent?",
      "How do I remind parents about a notice?",
      "How do I remind families about a notice?",
      "Remind parents about the notice i already sent",
      "Where is Send reminder?",
      "How do I nudge people who haven't opened a notice?",
      "How do I send a reminder for a notice?",
      "How do I ping people who didn't read a notice?",
      "How do reminders work?",
      "Where do I press Send reminder?",
      "How do I remind people about a sent notice?",
      "Can I send a reminder without writing a new notice?",
      "nudge people who havent opened it",
      "How do I remind people about one that's already out?",
      ...askedHow("send a reminder to people who have not voted"),
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "On Notices, under Sent, find the Notice and press Send reminder beside it. It goes to the original recipients, including people who already opened the Notice. Check the title, which starts with Reminder -, then press Send reminder. Nothing goes out until you press it.",
        () =>
          "Find the sent Notice and press Send reminder. It goes to the same people, including those who already opened it, with a title that starts Reminder -. You still confirm it before it sends.",
        () =>
          "Send reminder is beside a sent Notice on Notices. It goes to the original recipients, including people who already opened the Notice. You check the reminder and press Send reminder yourself.",
      ],
      link: linkToNotices,
    },
  }),

  defineEntry({
    id: "notices.pollResultsHowTo",
    sidebarItem: "notices",
    examples: [
      "Where do I see poll results?",
      "Where are the votes?",
      "Where can I see the votes?",
      "How do I see poll results?",
      "Show me poll results",
      "Where is the poll report?",
      "Who voted yes?",
      "How many people picked an option?",
      "Where do I see who voted?",
      "How do I open poll results?",
      "Where are the votes for a poll?",
      "How do I see the results of a poll?",
      "Where do I see the results?",
      "Can I close the poll early?",
      ...askedHow("see the results"),
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "Open the sent Poll from Notices. It opens on Poll Results, with the votes for each choice and Who Voted. There isn't a separate Polls page in the sidebar.",
        () =>
          "Poll results live on that Poll. Open it from Notices to see the votes and Who Voted.",
        () =>
          "From Notices, open the Poll. Poll Results shows each choice and who voted. The sidebar has no Polls item.",
      ],
      link: linkToNotices,
    },
  }),

  defineEntry({
    id: "notices.viewsHowTo",
    sidebarItem: "notices",
    examples: [
      "How do I see who has viewed a notice?",
      "Who read my notice?",
      "Where can I see who has seen my notice?",
      "How do I check readers?",
      "How do I check who read it?",
      "Where do I see who read my notice?",
      "Where do I see who has opened it?",
      "How do I see who viewed a notice?",
      "Where do I see who has read a notice?",
      "How do I check who opened a notice?",
      "Where are the views on a notice?",
      "How do I see read receipts?",
      "How do I see who has seen a notice?",
      "Where do I see awaiting views?",
      "How do I tell who read a notice?",
      "How do I see the viewers of a notice?",
      "who has seen the last notice",
      "did parents open it",
      "Who has seen it?",
      "how many people viewed it",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "Open Notices, then Sent, and open the Notice. The newest one is at the top. It shows who has viewed it and who is still awaiting.",
        () =>
          "To see who viewed a Notice, open Notices → Sent, open that Notice, and check the viewer list.",
        () =>
          "The views line is on each sent Notice. Open Notices → Sent and open the Notice to see who has viewed it and who is still awaiting.",
      ],
      link: linkToNotices,
    },
  }),

  defineEntry({
    id: "notices.senderHowTo",
    sidebarItem: "notices",
    examples: [
      "Where do I see notices from a particular sender?",
      "How do I filter notices by sender?",
      "Where is All Senders?",
      "How do I see what a teammate sent?",
      "Who posted this notice?",
      "Notices by sender",
      "How do I filter by who sent a notice?",
      "Can I see one person's notices?",
      "Where is the sender filter?",
      "How do I see notices from one teacher?",
      "filter by sender",
      "Show notices from one sender.",
      "who posted this",
      "Who sent it?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        (_values, _slots, asked = "") => senderHowToText(asked),
        (_values, _slots, asked = "") => senderHowToText(asked),
        (_values, _slots, asked = "") => senderHowToText(asked),
      ],
      link: linkToNotices,
    },
  }),

  defineEntry({
    id: "notices.circleFilterHowTo",
    sidebarItem: "notices",
    examples: [
      "Can I filter notices by circle?",
      "How do I see notices for one class?",
      "Where is the All Circles filter?",
      "Show notices for a group",
      "How do I filter the notice list by Circle?",
      "Is there a circle filter on Notices?",
      "Can I filter sent notices by Circle?",
      "How do I see one Circle's notices?",
      "Notices for one circle",
      "Does Notices have a circle filter?",
      "notices for one class",
      "Who was it sent to?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "Notices has an All Circles filter. That filters the list. It is not the Circles page, which a Creator can't open.",
        () => "Use All Circles on Notices to filter. Managing Circles is for an Owner or Admin.",
        () => "Yes. The All Circles filter is on Notices. A Creator still can't open the Circles page.",
      ],
      link: linkToNotices,
    },
  }),

  defineEntry({
    id: "notices.cantSeeHowTo",
    sidebarItem: "notices",
    examples: [
      "A parent says they can't see a notice. What do I check?",
      "A mom says the notice is not on her phone",
      "Why didn't one parent get the notice?",
      "The notice is missing for one parent",
      "What do I check if a parent can't see a notice?",
      "A parent can't find the notice",
      "One parent says they didn't get it",
      "What should I check when a parent can't see it?",
      "A family says the notice isn't there",
      "How do I check a missing notice for one parent?",
      "parents cant see the notice",
      "A parent says they can't see the notice I sent.",
      "why didnt they get the push",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "Check the Notice was published to their Circle or to Everyone, and that they've joined the app. If they can see the Notice but got no alert, ask them to check Noticeboard's notification permissions on their phone. Views alone don't confirm a push was delivered.",
        () =>
          "Look at who it was sent to and whether that parent has joined the app. A missing push can also be the phone's notification permissions. The views line doesn't prove a push arrived.",
        () =>
          "On Notices, check the audience was their Circle or Everyone, and that they have joined the app. If the Notice is visible but there was no alert, the phone's notification permissions are the next check.",
      ],
      link: linkToNotices,
    },
  }),

  // The Scheduled tab on Notices
  defineEntry<{ list: Dated[] }>({
    id: "notices.scheduled",
    sidebarItem: "notices",
    examples: [
      "Do we have any scheduled notices?",
      "How do I know if a notice is still waiting to go out?",
      "What notices are scheduled?",
      "Scheduled notices",
      "Which notices are going out later?",
      "What is scheduled to go out?",
      "Are any notices waiting to be published?",
      "How many notices are scheduled?",
      "When does my scheduled notice go out?",
      "List the scheduled notices",
      "Notices set to send later",
      "What notices go out next?",
      "Is anything scheduled to send?",
      "Anything scheduled",
      "pending notices",
      "queued notices",
      "Which ones are waiting to go out?",
      "how many are waiting to go out",
      "notices that arent sent yet but will be",
      "wheres scheduled notices",
      "How do I know if it was scheduled instead?",
    ],
    answer: {
      load: async (_, ctx) => {
        const { scheduled } = await loadFeed(ctx);
        return { list: scheduled.map((n) => ({ title: n.title, when: formatScheduleWhen(n.publishAt) })) };
      },
      templates: [
        ({ list }) =>
          list.length === 0
            ? "Open Notices and select Scheduled. You don't have any scheduled Notices waiting there."
            : `Open Notices and select Scheduled. You have ${scheduled(list.length)}: ${datedList(list)}.`,
        ({ list }) =>
          list.length === 0
            ? "Open Notices and select Scheduled. Nothing is scheduled to go out right now."
            : `Open Notices and select Scheduled. There ${isAre(list.length)} ${scheduled(list.length)}, in the order they go out: ${datedList(list)}.`,
        ({ list }) =>
          list.length === 0
            ? "Open Notices and select Scheduled. There are no scheduled Notices waiting to go out."
            : `Open Notices and select Scheduled. ${capitalize(scheduled(list.length))} ${isAre(list.length)} waiting to go out: ${datedList(list)}.`,
      ],
      link: () => ({ label: "See scheduled Notices", href: scheduledNoticesHref() }),
      mentionsOf: ({ list }) => list.map((item) => item.title),
    },
  }),

  // The Views on a Notice's card on Notices
  defineEntry<{ views: (Views & { notice: NoticeWithType }) | null }>({
    id: "notices.views",
    sidebarItem: "notices",
    examples: [
      "How many people viewed my last notice?",
      "How many people viewed the notice?",
      "How many people read the notice?",
      "How many people read my notice?",
      "How many views did the notice get?",
      "Have parents read the notice?",
      "Has everyone seen the notice?",
      "How many people opened my notice?",
      "Views on my latest notice",
      "How many have read the notice so far?",
      "What is the view count on the notice?",
      "Did families see the notice?",
      "Did parents see my last notice?",
      "How many people looked at the notice?",
      "How many people opened it?",
    ],
    slots: { notice: "" },
    answer: {
      load: async ({ notice: said = "" }, ctx) => {
        const pick = await findNamedNotice(said, ctx);
        if (!pick) return { views: null };
        if (isSlotQuestion(pick)) return pick;
        const { notice } = pick;
        return { views: { notice, title: notice.title, ...(await viewsOf(notice, ctx)) } };
      },
      templates: [
        ({ views }) =>
          views
            ? `${quoted(views.title)} has been viewed by ${viewsText(views)}.`
            : "You haven't sent any Notices yet, so there are no views to show.",
        ({ views }) =>
          views
            ? `So far, ${viewsText(views)} have viewed ${quoted(views.title)}.`
            : "There are no sent Notices to count views for yet.",
        ({ views }) =>
          views
            ? `Views on ${quoted(views.title)}: ${viewsText(views)}.`
            : "Once you send a Notice, I can tell you how many people viewed it.",
      ],
      link: (_, { views }) =>
        views ? noticeLink(views.notice) : linkToNotices(),
      subjectOf: ({ views }) => views?.title,
      mentionsOf: ({ views }) => (views ? [views.title] : []),
    },
  }),

  // How many haven't seen a sent Notice yet: its Viewers' total minus seen, matching Notice details
  defineEntry<{ unseen: { notice: NoticeWithType; title: string; seen: number; total: number; fromSummary?: boolean } | null }>({
    id: "notices.unseen",
    sidebarItem: "notices",
    examples: [
      "How many haven't seen my notice yet?",
      "How many people haven't seen the fees reminder notice?",
      "How many people haven't seen the notice yet?",
      "How many families still haven't seen the notice?",
      "How many are yet to see the notice?",
      "How many still need to see the camp letter notice?",
      "How many people haven't opened the notice yet?",
      "How many families haven't read the notice yet?",
      "Number of people who still haven't seen the notice",
      "How many are still to look at the notice?",
      "How many haven't checked the notice out yet?",
      "How many people are yet to open the notice?",
      "Who hasn't seen the notice?",
      "Who hasn't seen the welcome notice yet?",
      ...askedHow("see who has not seen a notice"),
    ],
    slots: { notice: "" },
    answer: {
      load: async ({ notice: said = "" }, ctx) => {
        const pick = await findNamedNotice(said, ctx);
        if (!pick) return { unseen: null };
        if (isSlotQuestion(pick)) return pick;
        const { notice } = pick;
        if (ctx.user?.schoolId && ctx.token && notice.noticeId) {
          try {
            const reads = await api.notices.getReads(notice.noticeId, ctx.user.schoolId, ctx.token, { targetSubcircle: notice.targetSubcircle });
            return { unseen: { notice, title: notice.title, ...normalizeViewCount({seen: reads.seen ?? reads.users?.length, total: reads.total}) } };
          } catch {
            const summary = await viewsOf(notice, ctx);
            return { unseen: { notice, title: notice.title, ...summary, fromSummary: true } };
          }
        }
        const summary = notice.audienceTotal === undefined ? normalizeViewCount({seen: notice.readCount, total: 0}) : await viewsOf(notice, ctx);
        return { unseen: { notice, title: notice.title, ...summary, fromSummary: true } };
      },
      templates: [
        ({ unseen }) =>
          !unseen
            ? NO_SENT
            : unseen.total - unseen.seen <= 0
              ? `Everyone has seen ${quoted(unseen.title)}.`
              : `${unseen.fromSummary ? "I couldn't load the latest Viewers list; based on the Notice summary, " : ""}${countOf(unseen.total - unseen.seen, "person", "people")} ${isAre(unseen.total - unseen.seen)} yet to see ${quoted(unseen.title)}.`,
        ({ unseen }) =>
          !unseen
            ? NO_SENT
            : unseen.total - unseen.seen <= 0
              ? `No one's left to see ${quoted(unseen.title)}: everyone has.`
              : `${unseen.fromSummary ? "I couldn't load the latest Viewers list; based on the Notice summary, " : ""}${quoted(unseen.title)} still hasn't been seen by ${countOf(unseen.total - unseen.seen, "person", "people")}.`,
        ({ unseen }) =>
          !unseen
            ? NO_SENT
            : unseen.total - unseen.seen <= 0
              ? `${quoted(unseen.title)} has been seen by everyone it went to.`
              : `${unseen.fromSummary ? "I couldn't load the latest Viewers list; based on the Notice summary, " : ""}Out of ${unseen.total}, ${countOf(unseen.total - unseen.seen, "person", "people")} ${isAre(unseen.total - unseen.seen)} still to see ${quoted(unseen.title)}.`,
      ],
      link: (_, { unseen }) => (unseen ? noticeLink(unseen.notice) : linkToNotices()),
      mentionsOf: ({ unseen }) => (unseen ? [unseen.title] : []),
    },
  }),

  // Who has seen a sent Notice, as its own Viewers list shows them
  defineEntry<{ viewers: { notice: NoticeWithType; names: string[]; total: number } | null }>({
    id: "notices.viewers",
    sidebarItem: "notices",
    examples: [
      "Who has seen my notice?",
      "Who's seen the camp letter?",
      "Who has seen the fees reminder?",
      "Which parents have seen the notice?",
      "Who has viewed the trip notice?",
      "Who opened the uniform notice?",
      "List who has seen the notice",
      "Who has read the excursion notice?",
      "Which families have viewed the notice?",
      "Show me who has seen the notice",
      "Who has looked at the sports day notice?",
      "Who's viewed the notice so far?",
    ],
    slots: { notice: "" },
    answer: {
      load: async ({ notice: said = "" }, ctx) => {
        const pick = await findNamedNotice(said, ctx);
        if (!pick) return { viewers: null };
        if (isSlotQuestion(pick)) return pick;
        const { notice } = pick;
        const { names, total } = await viewersOf(notice, ctx);
        return { viewers: { notice, names, total } };
      },
      templates: [
        ({ viewers }) =>
          !viewers
            ? NO_SENT
            : viewers.names.length === 0
              ? `No one has seen ${quoted(viewers.notice.title)} yet.`
              : `${quoted(viewers.notice.title)} has been seen by ${listUpTo(viewers.names)} (${viewers.names.length} of ${viewers.total}).`,
        ({ viewers }) =>
          !viewers
            ? NO_SENT
            : viewers.names.length === 0
              ? `${quoted(viewers.notice.title)} hasn't been seen by anyone yet.`
              : `Viewers of ${quoted(viewers.notice.title)} (${viewers.names.length} of ${viewers.total}): ${listUpTo(viewers.names)}.`,
        ({ viewers }) =>
          !viewers
            ? NO_SENT
            : viewers.names.length === 0
              ? `Nobody has opened ${quoted(viewers.notice.title)} so far.`
              : `So far, ${listUpTo(viewers.names)} ${isAre(viewers.names.length)} the ones who've seen ${quoted(viewers.notice.title)}.`,
      ],
      link: (_, { viewers }) => (viewers ? noticeLink(viewers.notice) : linkToNotices()),
      mentionsOf: ({ viewers }) => (viewers ? [viewers.notice.title] : []),
    },
  }),

  // A Poll's or an RSVP Event's choice results, as the Notice's own page shows them
  defineEntry<{
    result: { notice: NoticeWithType; choices: Choice[] | null; total: number; closed: boolean } | null;
  }>({
    id: "notices.pollResults",
    sidebarItem: "notices",
    examples: [
      "How many said yes to the concert?",
      "What did the uniform vote come out as?",
      "What were the poll results for the fundraiser?",
      "How many RSVPed yes to the trip?",
      "Who RSVPed to the swimming lesson?",
      "Is the poll for the fete closed?",
      "How did the vote on the theme go?",
      "What's the RSVP count for sports day?",
      "How many people are coming to the picnic?",
      "What were the results of the class poll?",
      "Show me the poll results for the fundraiser",
      "How many voted no in the poll?",
      "What's the breakdown of the uniform poll?",
      "How many people have voted?",
    ],
    slots: { notice: "" },
    answer: {
      load: async ({ notice: said = "" }, ctx) => {
        // "my last poll", with no Notice named: the newest one that has results, not the newest Notice
        const { sent } = await loadFeed(ctx);
        const unnamed = nameWordsOf(said).length === 0;
        const latestWithResults = unnamed ? sent.find(hasChoiceResults) : undefined;
        if (unnamed && !latestWithResults) return { result: null };
        const pick = latestWithResults ? { notice: latestWithResults } : await findNamedNotice(said, ctx);
        if (!pick) return { result: null };
        if (isSlotQuestion(pick)) return pick;
        const { notice } = pick;
        const { choices, total, closed } = await pollResultsOf(notice, ctx);
        return { result: { notice, choices, total, closed } };
      },
      templates: [
        ({ result }, _slots, asked = "") =>
          !result
            ? /vot|poll/i.test(asked)
              ? "There isn't a Poll among your sent Notices yet, so there are no results to show."
              : NO_SENT
            : !result.choices
              ? `${quoted(result.notice.title)} isn't a Poll or an RSVP Event, so there are no choice results to show.`
              : `I don't have names for who chose what, only counts. ${quoted(result.notice.title)}: ${choicesList(result.choices)}, out of ${result.total}. It's ${result.closed ? "closed" : "still open"}.`,
        ({ result }) =>
          !result
            ? NO_SENT
            : !result.choices
              ? `There's no poll or RSVP on ${quoted(result.notice.title)}.`
              : `So far, ${result.total} ${isAre(result.total)} answered ${quoted(result.notice.title)}: ${choicesList(result.choices)}. It's ${result.closed ? "closed" : "still open"}. I only have counts, not who chose what.`,
        ({ result }) =>
          !result
            ? NO_SENT
            : !result.choices
              ? `${quoted(result.notice.title)} doesn't take a Poll or RSVP answer.`
              : `${capitalize(result.notice.title)}'s results (${result.closed ? "closed" : "open"}): ${choicesList(result.choices)}, out of ${result.total}. Names aren't shown, only counts.`,
      ],
      link: (_, { result }) => (result ? noticeLink(result.notice) : linkToNotices()),
      mentionsOf: ({ result }) => (result ? [result.notice.title] : []),
    },
  }),

  // Notices with its Circle filter set
  defineEntry<{ circle: { circleId: string; name: string } | null; sent: NoticeWithType[] }>({
    id: "notices.toCircle",
    sidebarItem: "notices",
    examples: [
      "What notices have we sent to Grade 2?",
      "Which notices went to a circle?",
      "Notices sent to a circle",
      "How many notices has the circle had?",
      "Show me the notices for a class",
      "What did we send to the class?",
      "Notices for this circle",
      "How many notices went to the group?",
      "What has the circle been sent?",
      "List notices sent to a circle",
      "Which notices did the class get?",
      "Circle notices",
      "Find notices for Grade 2",
      "Show the notices for Grade 2",
    ],
    // "what did we send to testing today?": only the ones sent then
    slots: { circle: "", dateRange: "allTime" },
    answer: {
      load: async ({ circle: said = "", dateRange }, ctx) => {
        const [{ sent }, circles] = await Promise.all([loadFeed(ctx), loadCircles(ctx)]);
        if (circles.length === 0) return { circle: null, sent: [] };
        const pick = pickCircle(circles, said);
        if (isSlotQuestion(pick)) return pick;
        const { circleId, name } = pick.circle;
        const filters = { searchQuery: "", selectedCircle: circleId, selectedType: "all", selectedSender: "all" };
        const inRange = (n: NoticeWithType) => !dateRange || isInRange(sentAt(n), dateRange);
        return { circle: { circleId, name }, sent: filterNoticesForFeed(sent, filters, ctx.user).filter(inRange) };
      },
      templates: [
        ({ circle, sent }, { dateRange }) =>
          !circle
            ? NO_CIRCLES
            : sent.length === 0
              ? `You haven't sent any Notices to ${circle.name}${during(dateRange) || " yet"}.`
              : `You've sent ${notices(sent.length)} to ${circle.name}${during(dateRange)}: ${titlesOf(sent)}.`,
        ({ circle, sent }, { dateRange }) =>
          !circle
            ? NO_CIRCLES
            : sent.length === 0
              ? `${circle.name} hasn't been sent any Notices${during(dateRange) || " yet"}.`
              : `${circle.name} has been sent ${notices(sent.length)}${during(dateRange)}. The latest is ${quoted(sent[0].title)}.`,
        ({ circle, sent }, { dateRange }) =>
          !circle
            ? NO_CIRCLES
            : sent.length === 0
              ? `There are no sent Notices for ${circle.name}${during(dateRange) || " yet"}.`
              : `${capitalize(notices(sent.length))} went to ${circle.name}${during(dateRange)}, newest first: ${titlesOf(sent)}.`,
      ],
      link: (_, { circle }) =>
        circle
          ? { label: `See ${circle.name}'s Notices`, href: viewNoticesHref(circle.circleId) }
          : linkToNotices(),
      mentionsOf: ({ sent }) => sent.map((n) => n.title),
    },
  }),

  // Notices with the Type filter set to Urgent, on both tabs
  defineEntry<{ sent: string[]; scheduled: Dated[] }>({
    id: "notices.urgent",
    sidebarItem: "notices",
    examples: [
      "Which notices are urgent?",
      "Show me all urgent notices",
      "Find urgent notices",
      "Is anything urgent going out today?",
      "List the urgent notices",
      "Urgent notices",
      "Have we sent any urgent notices?",
      "What urgent notices went out this week?",
      "Are any urgent notices scheduled?",
      "Show urgent notices only",
      "Which notices did we mark as urgent?",
      "Any urgent messages today?",
      "What urgent announcements have we sent?",
    ],
    slots: { dateRange: "allTime" },
    answer: {
      load: async ({ dateRange }, ctx) => {
        const feed = await loadFeed(ctx);
        const inRange = (iso?: string) => !dateRange || (iso ? isInRange(iso, dateRange) : false);
        return {
          sent: feed.sent.filter((n) => n.isUrgent && inRange(sentAt(n))).map((n) => n.title),
          scheduled: feed.scheduled
            .filter((n) => n.isUrgent && inRange(n.publishAt))
            .map((n) => ({ title: n.title, when: formatScheduleWhen(n.publishAt) })),
        };
      },
      templates: [
        ({ sent, scheduled }, { dateRange }) => {
          const n = sent.length + scheduled.length;
          return n === 0
            ? `There are no Urgent Notices${during(dateRange)}.`
            : `There ${isAre(n)} ${urgent(n)}${during(dateRange)}: ${urgentList(sent, scheduled)}.`;
        },
        ({ sent, scheduled }, { dateRange }) => {
          const n = sent.length + scheduled.length;
          return n === 0
            ? `Nothing urgent: you have no Urgent Notices${during(dateRange)}.`
            : `You have ${urgent(n)}${during(dateRange)}: ${urgentList(sent, scheduled)}.`;
        },
        ({ sent, scheduled }, { dateRange }) => {
          const n = sent.length + scheduled.length;
          return n === 0
            ? `I can't see any Urgent Notices${during(dateRange)}.`
            : `${capitalize(urgent(n))}${during(dateRange)}: ${urgentList(sent, scheduled)}.`;
        },
      ],
      link: () => ({ label: "See Urgent Notices", href: urgentNoticesHref() }),
      mentionsOf: ({ sent, scheduled }) => [...sent, ...scheduled.map((item) => item.title)],
    },
  }),

  // Notices with the Sender filter set to one Team member
  defineEntry<{ sender: string | null; sent: NoticeWithType[] }>({
    id: "notices.bySender",
    sidebarItem: "notices",
    examples: [
      "Which notices did a teacher create?",
      "What notices has a team member sent?",
      "Notices sent by a teacher",
      "Show me the notices she created",
      "What has he sent?",
      "Which notices did I create?",
      "Show me my notices",
      "List the notices a staff member wrote",
      "How many notices has the principal sent?",
      "Notices created by a colleague",
      "Who sent which notices?",
      "Filter notices by sender",
    ],
    slots: { sender: "", dateRange: "allTime" },
    answer: {
      load: async ({ sender: said = "", dateRange }, ctx) => {
        const { sent } = await loadFeed(ctx);
        const senderOf = (n: NoticeWithType) => getReadableSenderName(n.sender, ctx.user);
        const senders = Array.from(new Set(sent.map(senderOf))).sort();
        if (senders.length === 0) return { sender: null, sent: [] };
        const me = ctx.user?.fullName || ctx.user?.displayName;
        const pick = pickSender(senders, said, me);
        if (isSlotQuestion(pick)) return pick;
        const { sender } = pick;
        return {
          sender,
          sent: sent.filter((n) => senderOf(n) === sender && (!dateRange || isInRange(sentAt(n), dateRange))),
        };
      },
      templates: [
        ({ sender, sent }, { dateRange }) =>
          !sender
            ? NO_SENT
            : sent.length === 0
              ? countSentence(dateRange, `${sender} hasn't sent any Notices${during(dateRange)}.`)
              : countSentence(dateRange, `${sender} has sent ${notices(sent.length)}${during(dateRange)}: ${titlesOf(sent)}.`),
        ({ sender, sent }, { dateRange }) =>
          !sender
            ? NO_SENT
            : sent.length === 0
              ? countSentence(dateRange, `there are no Notices from ${sender}${during(dateRange)}.`)
              : countSentence(dateRange, `${capitalize(notices(sent.length))} ${wasWere(sent.length)} sent by ${sender}${during(dateRange)}, newest first: ${titlesOf(sent)}.`),
        ({ sender, sent }, { dateRange }) =>
          !sender
            ? NO_SENT
            : sent.length === 0
              ? countSentence(dateRange, `${sender} hasn't sent a Notice${during(dateRange)}.`)
              : countSentence(dateRange, `${sender} created ${notices(sent.length)}${during(dateRange)}. The latest is ${quoted(sent[0].title)}.`),
      ],
      link: (_, { sender }) =>
        sender ? { label: `See ${sender}'s Notices`, href: noticesBySenderHref(sender) } : linkToNotices(),
      mentionsOf: ({ sent }) => sent.map((n) => n.title),
    },
  }),

  // Who sent the most Notices, in a date range or ever
  defineEntry<{ top: { sender: string; count: number } | null }>({
    id: "notices.topSender",
    sidebarItem: "notices",
    examples: [
      "Who sent the most notices?",
      "Who's sent the most this month?",
      "Which team member has sent the most notices?",
      "Who sent the most notices this week?",
      "Who has sent the most notices overall?",
      "Which teacher sends the most notices?",
      "Who's our most active sender?",
      "Who sends the most notices to parents?",
      "Which team member sent the most notices last month?",
      "Who's sent the most notices this year?",
      "Who has created the most notices?",
      "Who sends the most?",
    ],
    slots: { dateRange: "allTime" },
    answer: {
      load: async ({ dateRange }, ctx) => {
        const { sent } = await loadFeed(ctx);
        const inRange = sent.filter((n) => !dateRange || isInRange(sentAt(n), dateRange));
        if (inRange.length === 0) return { top: null };
        const senderOf = (n: NoticeWithType) => getReadableSenderName(n.sender, ctx.user);
        const counts = new Map<string, number>();
        inRange.forEach((n) => counts.set(senderOf(n), (counts.get(senderOf(n)) ?? 0) + 1));
        const [sender, count] = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0];
        return { top: { sender, count } };
      },
      templates: [
        ({ top }, { dateRange }) =>
          !top
            ? `No Notices have been sent${during(dateRange)}.`
            : countSentence(dateRange, `${top.sender} has sent the most${during(dateRange)}: ${notices(top.count)}.`),
        ({ top }, { dateRange }) =>
          !top
            ? `There's no one to name: no Notices have gone out${during(dateRange)}.`
            : countSentence(dateRange, `${top.sender} sent the most Notices${during(dateRange)}, with ${notices(top.count)}.`),
        ({ top }, { dateRange }) =>
          !top
            ? `Nothing has been sent${during(dateRange)}, so there's no top sender.`
            : countSentence(dateRange, `${top.sender} is the top sender${during(dateRange)}: ${notices(top.count)}.`),
      ],
      link: (_, { top }) =>
        top ? { label: `See ${top.sender}'s Notices`, href: noticesBySenderHref(top.sender) } : linkToNotices(),
    },
  }),

  // The sent Notice with the most views, in a date range or ever
  defineEntry<{ top: { notice: NoticeWithType; seen: number } | null }>({
    id: "notices.mostViewed",
    sidebarItem: "notices",
    examples: [
      "Which notice got the most views this month?",
      "Which notice has been seen the most?",
      "What's our most viewed notice?",
      "Which notice got the most views last week?",
      "What notice has had the most views this year?",
      "Which notice was seen the most?",
      "What's the most popular notice we've sent?",
      "Which notice has the highest view count?",
      "Which notice got the most views last month?",
      "What notice have the most people seen?",
      "Which of our notices was viewed the most?",
      "Which notice was viewed the most?",
    ],
    slots: { dateRange: "allTime" },
    answer: {
      load: async ({ dateRange }, ctx) => {
        const { sent } = await loadFeed(ctx);
        const inRange = sent.filter((n) => !dateRange || isInRange(sentAt(n), dateRange));
        if (inRange.length === 0) return { top: null };
        const top = inRange.reduce((best, n) => ((n.readCount ?? 0) > (best.readCount ?? 0) ? n : best));
        return { top: { notice: top, seen: top.readCount ?? 0 } };
      },
      templates: [
        ({ top }, { dateRange }) =>
          !top
            ? `You haven't sent any Notices${during(dateRange)}.`
            : countSentence(
                dateRange,
                `${quoted(top.notice.title)} got the most views${during(dateRange)}, seen by ${countOf(top.seen, "person", "people")}.`
              ),
        ({ top }, { dateRange }) =>
          !top
            ? `There's nothing to compare: no Notices have been sent${during(dateRange)}.`
            : countSentence(
                dateRange,
                `${quoted(top.notice.title)} was viewed the most${during(dateRange)}: ${countOf(top.seen, "person", "people")}.`
              ),
        ({ top }, { dateRange }) =>
          !top
            ? `No Notices${during(dateRange)} to show a most-viewed one for.`
            : countSentence(
                dateRange,
                `${quoted(top.notice.title)} has had the most views${during(dateRange)}, with ${countOf(top.seen, "person", "people")}.`
              ),
      ],
      link: (_, { top }) => (top ? noticeLink(top.notice) : linkToNotices()),
      mentionsOf: ({ top }) => (top ? [top.notice.title] : []),
    },
  }),

  // The Sent tab, for the Notices that went out in a date range
  defineEntry<{ sent: NoticeWithType[] }>({
    id: "notices.sentOn",
    sidebarItem: "notices",
    examples: [
      "What did we send last Friday?",
      "What did we send today?",
      "What went out yesterday?",
      "Where did my notice from yesterday go?",
      "What did I send yesterday?",
      "Which notices did we send on Monday?",
      "What notices were sent last week?",
      "Show me what we sent this week",
      "What did I send yesterday?",
      "List the notices sent today",
      "Which notices went out last month?",
      "What have we sent so far this month?",
      "What did we send last year?",
      "total notices?",
      "How many notices have we sent?",
      "How many notices have we sent in total?",
      "Have we sent many notices?",
      "How many notices do we have?",
      "How many notices are there?",
      "How many have we sent?",
      "how many announcements are there",
      "whats our notice count",
      "What did we send this term?",
      "Which notices went out this term?",
      ...askedHow("see notices sent today"),
    ],
    slots: { dateRange: "allTime" },
    answer: {
      load: async ({ dateRange }, ctx) => {
        const { sent } = await loadFeed(ctx);
        return { sent: sent.filter((n) => dateRange && isInRange(sentAt(n), dateRange)) };
      },
      templates: [
        ({ sent }, { dateRange }) =>
          countSentence(
            dateRange,
            sent.length === 0
              ? `you didn't send any Notices${during(dateRange)}.`
              : `you sent ${notices(sent.length)}${during(dateRange)}: ${titlesOf(sent)}.`
          ) + offerThisWeek(dateRange),
        ({ sent }, { dateRange }) =>
          countSentence(
            dateRange,
            sent.length === 0
              ? `no Notices went out${during(dateRange)}.`
              : `${notices(sent.length)} went out${during(dateRange)}, newest first: ${titlesOf(sent)}.`
          ) + offerThisWeek(dateRange),
        ({ sent }, { dateRange }) =>
          countSentence(
            dateRange,
            sent.length === 0
              ? `nothing was sent${during(dateRange)}.`
              : `you sent ${notices(sent.length)}${during(dateRange)}: ${titlesOf(sent)}.`
          ) + offerThisWeek(dateRange),
      ],
      link: (_, { sent }) => (sent.length === 1 ? noticeLink(sent[0]) : linkToNotices()),
      mentionsOf: ({ sent }) => sent.map((n) => n.title),
    },
  }),

  // When a named sent Notice is: an Event's day and time, or when any other Notice went out
  defineEntry<{ notice: NoticeWithType | null; none?: boolean }>({
    id: "notices.when",
    sidebarItem: "notices",
    examples: [
      "When is the sports day notice?",
      "When is the dinner?",
      "What time is the concert?",
      "What day is the trip notice?",
      "When was the fees notice sent?",
      "When does the picnic happen?",
      "What date is the swimming notice?",
      "When is that event on?",
      "Tell me when the notice is",
      "When is the art show?",
      "What time does the meeting notice start?",
      "When did the welcome notice go out?",
    ],
    slots: { notice: "" },
    answer: {
      load: async ({ notice: said = "" }, ctx) => {
        const pick = await findNoticeForWhen(said, ctx);
        if (isSlotQuestion(pick)) return pick;
        if (pick) return { notice: pick.notice, none: false };
        // "You haven't sent any" only when that's true; otherwise the name just didn't match one
        const { sent, scheduled } = await loadFeed(ctx);
        return { notice: null, none: sent.length + scheduled.length === 0 };
      },
      templates: [
        ({ notice, none }) => (notice ? whenWordings(notice)[0] : none ? NO_SENT : NOT_SURE_WHICH),
        ({ notice, none }) => (notice ? whenWordings(notice)[1] : none ? NO_SENT : NOT_SURE_WHICH),
        ({ notice, none }) => (notice ? whenWordings(notice)[2] : none ? "There are no sent Notices to give a time for yet." : NOT_SURE_WHICH),
      ],
      link: (_, { notice }) => (notice ? noticeLink(notice) : linkToNotices()),
      subjectOf: ({ notice }) => notice?.title,
      mentionsOf: ({ notice }) => (notice ? [notice.title] : []),
    },
  }),

  sendReminderEntry,
];
